import React, { useState, useEffect } from "react";
import { Global } from "@emotion/react";
import { styled } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import { grey } from "@mui/material/colors";
import SwipeableDrawer from "@mui/material/SwipeableDrawer";
import { useAppContext } from "../../AppContext";
import axios from "axios";
import { MyDomain } from "../../App";
import { useParams } from "react-router-dom";
import * as signalR from "@microsoft/signalr";
import { useTranslation } from "react-i18next";

const C = {
  primary: "#1A1A2E",
  primaryHover: "#16213E",
  accent: "#C9A84C",
  accentLight: "#FDF6E3",
  accentBorder: "rgba(201,168,76,0.22)",
  accentMuted: "rgba(201,168,76,0.12)",
  border: "#e2e8f0",
  textMuted: "#64748b",
  white: "#ffffff",
};

const Root = styled("div")(({ theme }) => ({
  height: "100%",
  backgroundColor: grey[100],
}));

const StyledBox = styled("div")(({ theme }) => ({
  backgroundColor: C.white,
  borderTopLeftRadius: "24px",
  borderTopRightRadius: "24px",
}));

const Puller = styled("div")(({ theme }) => ({
  width: 36,
  height: 4,
  backgroundColor: C.accent,
  borderRadius: 2,
  position: "absolute",
  top: 12,
  left: "calc(50% - 18px)",
  opacity: 0.5,
}));

function MPHomeModalMakeApp(props) {
  const { t } = useTranslation();
  const { BarberPhoneNumber } = useParams();

  const {
    OpenDrawer,
    setOpenDrawer,
    ObjectData,
    setSelectedServices,
    SelectedServices,
    User,
    AllData,
    ChoosedApps,
    triggerRefresh,
    setValue,
    setStep,
    step,
    Lang,
  } = useAppContext();

  const getCurrencySymbol = (countryCode) => {
    const currencyMap = {
      "+972": "₪",
      "+46": "kr",
      "+1": "$",
      "+44": "£",
      "+49": "€",
    };
    return currencyMap[countryCode] || "₪";
  };

  const currencySymbol = getCurrencySymbol(User?.countryNumber);
  const isRTL = Lang === "ar" || Lang === "he";
  const { window } = props;
  const [IsLoading, setIsLoading] = useState(false);

  const toggleDrawer = (newOpen) => () => setOpenDrawer(newOpen);

  const container =
    window !== undefined ? () => window().document.body : undefined;

  const totalDuration = SelectedServices.reduce(
    (sum, service) => sum + (service.Duration || 0),
    0,
  );

  const calculateActualEndTime = () => {
    if (!ObjectData?.timeStart || totalDuration === 0) {
      return ObjectData?.timeEnd || "";
    }
    const [hours, minutes] = ObjectData.timeStart.split(":").map(Number);
    const totalMinutes = hours * 60 + minutes + totalDuration;
    const endHours = Math.floor(totalMinutes / 60);
    const endMinutes = totalMinutes % 60;
    return `${String(endHours).padStart(2, "0")}:${String(endMinutes).padStart(2, "0")}`;
  };

  const actualEndTime = calculateActualEndTime();

  const BookApp = async () => {
    if (!User || !User.name || !User.phoneNumber) {
      alert("Please login first to book an appointment.");
      setOpenDrawer(false);
      return;
    }

    setIsLoading(true);

    const findAdditionalAppointments = () => {
      const additionalAppIds = [];
      let accumulatedTime = ObjectData.timeLength;
      let currentIndex = -1;

      for (let i = 0; i < ChoosedApps.length; i++) {
        if (ChoosedApps[i].id === ObjectData.id) {
          currentIndex = i;
          break;
        }
      }

      if (currentIndex === -1 || accumulatedTime >= totalDuration) return additionalAppIds;

      let nextIndex = currentIndex + 1;
      while (nextIndex < ChoosedApps.length && accumulatedTime < totalDuration) {
        const nextApp = ChoosedApps[nextIndex];
        const prevApp = ChoosedApps[nextIndex - 1];
        if (prevApp.timeEnd !== nextApp.timeStart) break;
        if (nextApp.isTaken === 1) break;
        additionalAppIds.push(nextApp.id);
        accumulatedTime += nextApp.timeLength;
        nextIndex++;
      }

      return additionalAppIds;
    };

    const additionalAppIds = findAdditionalAppointments();

    const data2 = {
      Appointment: {
        Id: ObjectData.id,
        PhoneNumber: User.phoneNumber,
        Name: User.name,
        IsTaken: 1,
        Date: ObjectData.date,
        TimeStart: ObjectData.timeStart,
        TimeEnd: actualEndTime,
        TimeLength: totalDuration,
        BarberPhoneNumber: ObjectData.barberPhoneNumber,
      },
      Barber: {
        ExpoToken: ObjectData.selectedBarber?.expoToken || AllData.barberData.expoToken,
      },
      Lang: Lang,
      BookedServices: SelectedServices,
      AllServices: SelectedServices,
      AdditionalAppointmentIds: additionalAppIds,
    };

    try {
      await axios.put(`${MyDomain}Appointments2/UpdateAppWithServices`, data2, {
        timeout: 10000,
        headers: { "Content-Type": "application/json" },
      });

      setValue(1);
      setStep(5);
      setSelectedServices([]);

      setTimeout(() => setOpenDrawer(false), 100);
    } catch (error) {
      if (error.code === "ECONNABORTED") {
        alert("Request timeout. Please check your internet connection.");
      } else if (error.response) {
        alert("Booking failed: someone already booked this app");
      } else if (error.request) {
        alert("Network error. Please check your internet connection.");
      } else {
        alert("Failed to book appointment. Please try again.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const totalPrice = SelectedServices.reduce(
    (sum, service) => sum + parseFloat(service.Price || 0),
    0,
  );

  useEffect(() => {
    if (!BarberPhoneNumber && !ObjectData?.barberPhoneNumber) return;

    const barberPhone = ObjectData?.barberPhoneNumber || BarberPhoneNumber;

    const connection = new signalR.HubConnectionBuilder()
      .withUrl(`${MyDomain}appointmentHub`)
      .withAutomaticReconnect({
        nextRetryDelayInMilliseconds: (retryContext) => {
          if (retryContext.elapsedMilliseconds < 60000) return Math.random() * 10000;
          return null;
        },
      })
      .configureLogging(signalR.LogLevel.Information)
      .build();

    connection.onclose(() => {});
    connection.onreconnecting(() => {});
    connection.onreconnected(() => {});

    const startConnection = async () => {
      try {
        if (connection.state === signalR.HubConnectionState.Disconnected) {
          await connection.start();
          connection.on("ReceiveBookingUpdate", () => setOpenDrawer(false));
          connection.on("AppointmentCreated", () => setOpenDrawer(false));
        }
      } catch (err) {
        setTimeout(() => {
          if (connection.state === signalR.HubConnectionState.Disconnected) startConnection();
        }, 5000);
      }
    };

    startConnection();

    return () => {
      if (connection) {
        connection.off("ReceiveBookingUpdate");
        connection.off("AppointmentCreated");
        const state = connection.state;
        if (
          state === signalR.HubConnectionState.Connected ||
          state === signalR.HubConnectionState.Connecting
        ) {
          connection.stop().catch((err) => {
            if (!err.message?.includes("connection being closed")) {
              console.log("⚠️ Error stopping Modal SignalR:", err.message);
            }
          });
        }
      }
    };
  }, [BarberPhoneNumber, ObjectData?.barberPhoneNumber, setOpenDrawer]);

  // ── Info card ────────────────────────────────────────────────────────────

  const InfoCard = ({ icon, label, value }) => (
    <div
      style={{
        textAlign: "center",
        padding: "16px 8px",
        background: C.accentLight,
        borderRadius: "14px",
        border: `1px solid ${C.accentBorder}`,
      }}
    >
      <div style={{ fontSize: "22px", marginBottom: "8px" }}>{icon}</div>
      <div
        style={{
          fontSize: "11px",
          color: C.textMuted,
          marginBottom: "5px",
          fontWeight: "600",
          textTransform: "uppercase",
          letterSpacing: "0.5px",
        }}
      >
        {label}
      </div>
      <div style={{ fontSize: "13px", fontWeight: "700", color: C.primary }}>
        {value}
      </div>
    </div>
  );

  return (
    <Root>
      <CssBaseline />
      <Global
        styles={{
          "@import":
            "url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap')",
          ".MuiDrawer-root > .MuiPaper-root": {
            height: "auto",
            maxHeight: "90%",
            overflow: "visible",
            borderTopLeftRadius: "24px",
            borderTopRightRadius: "24px",
            boxShadow: "0 -8px 32px rgba(26,26,46,0.15)",
          },
          "*": {
            WebkitTapHighlightColor: "transparent",
            WebkitTouchCallout: "none",
            fontFamily: "'Inter', -apple-system, sans-serif",
          },
        }}
      />
      <SwipeableDrawer
        container={container}
        anchor="bottom"
        open={OpenDrawer}
        onClose={toggleDrawer(false)}
        onOpen={toggleDrawer(true)}
        swipeAreaWidth={0}
        disableSwipeToOpen={true}
        ModalProps={{ keepMounted: true }}
        sx={{ zIndex: 1050 }}
      >
        <Puller />
        <StyledBox sx={{ p: 3, pt: 4, direction: isRTL ? "rtl" : "ltr" }}>

          {/* Header */}
          <div style={{ marginBottom: "24px", textAlign: "center" }}>
            {/* Gold accent bar */}
            <div
              style={{
                width: "40px",
                height: "3px",
                background: C.accent,
                borderRadius: "2px",
                margin: "0 auto 14px",
              }}
            />
            <h2
              style={{
                fontSize: "22px",
                fontWeight: "700",
                color: C.primary,
                margin: "0 0 6px 0",
                letterSpacing: "-0.3px",
              }}
            >
              {t("ConfirmYourBooking")}
            </h2>
            <p style={{ fontSize: "14px", color: C.textMuted, margin: 0 }}>
              {t("ReviewAppointmentDetails")}
            </p>
          </div>

          {/* Appointment Info Cards */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(3, 1fr)",
              gap: "10px",
              marginBottom: "20px",
            }}
          >
            <InfoCard icon="📅" label={t("Date")} value={ObjectData.date} />
            <InfoCard icon="🕐" label={t("Time")} value={ObjectData.timeStart} />
            <InfoCard icon="⏱️" label={t("Duration")} value={`${totalDuration} ${t("Min")}`} />
          </div>

          {/* Services List */}
          {SelectedServices.length > 0 && (
            <div style={{ marginBottom: "20px" }}>
              <div
                style={{
                  fontSize: "11px",
                  fontWeight: "700",
                  color: C.accent,
                  marginBottom: "10px",
                  textAlign: isRTL ? "right" : "left",
                  textTransform: "uppercase",
                  letterSpacing: "0.8px",
                }}
              >
                {t("SelectedServicesUpper")}
              </div>

              <div
                style={{
                  background: "#fafafa",
                  borderRadius: "14px",
                  padding: "4px 12px",
                  border: `1px solid ${C.border}`,
                  overflow: "hidden",
                }}
              >
                {SelectedServices.map((service, index) => (
                  <div
                    key={index}
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      padding: "12px 0",
                      borderBottom:
                        index < SelectedServices.length - 1
                          ? `1px solid ${C.border}`
                          : "none",
                      direction: isRTL ? "rtl" : "ltr",
                    }}
                  >
                    <div style={{ flex: 1, textAlign: isRTL ? "right" : "left" }}>
                      <div
                        style={{
                          fontSize: "14px",
                          fontWeight: "600",
                          color: C.primary,
                          marginBottom: "2px",
                        }}
                      >
                        {service.Service}
                      </div>
                      {service.Duration && (
                        <div style={{ fontSize: "12px", color: C.textMuted }}>
                          {service.Duration} {t("Min")}
                        </div>
                      )}
                    </div>
                    <div
                      style={{
                        fontSize: "15px",
                        fontWeight: "700",
                        color: C.primary,
                        marginLeft: isRTL ? 0 : "16px",
                        marginRight: isRTL ? "16px" : 0,
                        whiteSpace: "nowrap",
                      }}
                    >
                      {service.Price} {currencySymbol}
                    </div>
                  </div>
                ))}

                {/* Total row */}
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    paddingTop: "12px",
                    paddingBottom: "4px",
                    marginTop: "4px",
                    borderTop: `2px solid ${C.accentBorder}`,
                    direction: isRTL ? "rtl" : "ltr",
                  }}
                >
                  <div
                    style={{ fontSize: "15px", fontWeight: "700", color: C.primary }}
                  >
                    {t("TotalPrice")}
                  </div>
                  <div
                    style={{ fontSize: "20px", fontWeight: "700", color: C.accent }}
                  >
                    {totalPrice.toFixed(2)} {currencySymbol}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div
            style={{
              display: "flex",
              gap: "10px",
              flexDirection: isRTL ? "row-reverse" : "row",
            }}
          >
            {/* Cancel */}
            <button
              onClick={toggleDrawer(false)}
              disabled={IsLoading}
              style={{
                flex: 1,
                padding: "14px",
                fontSize: "15px",
                fontWeight: "600",
                border: `1.5px solid ${C.border}`,
                borderRadius: "12px",
                backgroundColor: C.white,
                color: C.textMuted,
                cursor: IsLoading ? "not-allowed" : "pointer",
                opacity: IsLoading ? 0.5 : 1,
                transition: "all 0.2s ease",
                outline: "none",
              }}
              onMouseEnter={(e) => {
                if (!IsLoading) {
                  e.currentTarget.style.borderColor = C.accent;
                  e.currentTarget.style.color = C.accent;
                }
              }}
              onMouseLeave={(e) => {
                if (!IsLoading) {
                  e.currentTarget.style.borderColor = C.border;
                  e.currentTarget.style.color = C.textMuted;
                }
              }}
            >
              {t("Cancel")}
            </button>

            {/* Confirm */}
            <button
              onClick={BookApp}
              disabled={IsLoading}
              style={{
                flex: 2,
                padding: "14px",
                fontSize: "15px",
                fontWeight: "700",
                border: "none",
                borderRadius: "12px",
                backgroundColor: IsLoading ? "#94a3b8" : C.primary,
                color: IsLoading ? C.white : C.accent,
                cursor: IsLoading ? "not-allowed" : "pointer",
                transition: "all 0.2s ease",
                boxShadow: IsLoading ? "none" : "0 4px 16px rgba(26,26,46,0.35)",
                outline: "none",
                letterSpacing: "0.3px",
              }}
              onMouseEnter={(e) => {
                if (!IsLoading) {
                  e.currentTarget.style.backgroundColor = C.primaryHover;
                  e.currentTarget.style.boxShadow = "0 6px 20px rgba(26,26,46,0.45)";
                  e.currentTarget.style.transform = "translateY(-1px)";
                }
              }}
              onMouseLeave={(e) => {
                if (!IsLoading) {
                  e.currentTarget.style.backgroundColor = C.primary;
                  e.currentTarget.style.boxShadow = "0 4px 16px rgba(26,26,46,0.35)";
                  e.currentTarget.style.transform = "translateY(0)";
                }
              }}
            >
              {IsLoading ? (
                <span
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "8px",
                  }}
                >
                  <span
                    style={{
                      width: "14px",
                      height: "14px",
                      border: `2px solid rgba(201,168,76,0.3)`,
                      borderTopColor: C.accent,
                      borderRadius: "50%",
                      animation: "spin 0.8s linear infinite",
                    }}
                  />
                  {t("BookingInProgress")}
                </span>
              ) : (
                t("ConfirmBooking")
              )}
            </button>
          </div>
        </StyledBox>
      </SwipeableDrawer>

      <style>
        {`@keyframes spin { to { transform: rotate(360deg); } }`}
      </style>
    </Root>
  );
}

export default MPHomeModalMakeApp;
